import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';

import { AppState } from 'src/app/store/app.reducer';
import { User } from 'src/app/model/user';
import { Device } from 'src/app/model/device';
import { DeviceModel } from 'src/app/model/device-model';
import { DeviceInfo } from 'src/app/model/device-info';
import { DeviceState, DeviceStatus } from 'src/app/model/device-status';

import { selectUser } from 'src/app/store/user/user.selector';
import { selectCurrentDevice, selectCurrentDeviceModel, selectCurrentDeviceInfo, selectCurrentDeviceStatus, selectStateDeviceByCommandId, selectIsSupportTimers, selectAutomationsFirmwaresList, selectHwVersion, selectFirmwareUpdate } from 'src/app/store/device/device.selector';
import { DeviceCategories, EDeviceCategory } from 'src/app/conf/device-categories';
import { EDeviceModel, ExcludeModelBoardType, ExcludeModelFwVersion, ExcludeModelHwVersion, isMtmacModel, isRbe4BoardName, isThOneBt, isThOneGw, isZbb3BoardName, isZlb24sBoardName, isZlx24sBoardName, isZn8BoardName } from 'src/app/conf/device-models';
import { TranslateService } from '@ngx-translate/core';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { StandardDesignParameters } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { Firmware, FirmwaresCategory, FirmwareUpdate } from 'src/app/model/firmware-update';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ReleaseNoteDeviceDialogComponent } from 'src/app/modules/shared/components/release-note-device-dialog/release-note-device.dialog.component';
import { CLOUD_FW_UPGRADE_STATE } from 'src/app/conf/firmware-upgrade';
import { UpdateFirmwareDialogComponent } from '../update-firmware-dialog/update-firmware.dialog.component';

@Component({
	selector: 'app-device-info',
	templateUrl: './device-info.component.html',
	providers: [DialogService]
})
export class DeviceInfoComponent implements OnInit, OnDestroy {

	// Observables and subscriptions
	user$: Observable<User | undefined>;
	userSub: Subscription | undefined;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;
	deviceModel$: Observable<DeviceModel | undefined>;
	deviceModelSub: Subscription | undefined;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;
	deviceStatus$: Observable<DeviceStatus | undefined>;
	deviceStatusSub: Subscription | undefined;
	isSupportTimers$: Observable<boolean | undefined>;
	isSupportTimersSub: Subscription | undefined;
	automationsFirmwaresList$: Observable<FirmwaresCategory | undefined>;
	automationsFirmwaresListSub: Subscription | undefined;
	hwVersion$: Observable<number | undefined>;
	hwVersionSub: Subscription | undefined;
	firmwareUpdate$: Observable<FirmwareUpdate[] | undefined>;
	firmwareUpdateSub: Subscription | undefined;

	firmwareUpdates: FirmwareUpdate[] = [];


	stateDeviceByCommandId$: Observable<DeviceState | undefined>
	stateDeviceByCommandIdSub: Subscription | undefined;
	deviceOnline: boolean;
	deviceConfigured: boolean;

	automationsFirmwaresList: FirmwaresCategory;

	user: User;
	device: Device;
	deviceModel: DeviceModel;
	deviceInfo: DeviceInfo;
	deviceStatus: DeviceStatus;
	isSupportTimers = false;
	releaseNoteAvailable: boolean;
	releaseNoteList: Firmware[];

	categoryName = '';

	updatingFirmware = false;
	ref: DynamicDialogRef | undefined;

	screenWidth: number;

	COMMAND_ID_FW_UPDATE = 16;
	hwVer = null;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	ngOnInit(): void {
		this.releaseNoteList = [];
		this.releaseNoteAvailable = false;
		this.subscribeAll();
		this.onResize();
	}

	initSelectors(): void {
		// Store selectors
		this.user$ = this.store.select(selectUser);
		this.device$ = this.store.select(selectCurrentDevice);
		this.deviceModel$ = this.store.select(selectCurrentDeviceModel);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.deviceStatus$ = this.store.select(selectCurrentDeviceStatus);
		this.stateDeviceByCommandId$ = this.store.select(selectStateDeviceByCommandId);
		this.isSupportTimers$ = this.store.select(selectIsSupportTimers);
		this.automationsFirmwaresList$ = this.store.select(selectAutomationsFirmwaresList);
		this.hwVersion$ = this.store.select(selectHwVersion);
		this.firmwareUpdate$ = this.store.select(selectFirmwareUpdate);
	}

	subscribeAll(): void {
		this.firmwareUpdateSub = this.firmwareUpdate$.subscribe(firmwareUpdates => {
			this.firmwareUpdates = firmwareUpdates.filter(fir => fir.AvailableFWVersion);
		});
		this.automationsFirmwaresListSub = this.automationsFirmwaresList$?.subscribe(
			(automationsFirmwaresList) => {
				this.automationsFirmwaresList = automationsFirmwaresList;
				this.deviceFirmware();
			}
		);
		this.userSub = this.user$?.subscribe(
			(user) => {
				this.user = user;
			}
		);
		this.deviceSub = this.device$?.subscribe(
			(device) => {
				if (device !== undefined && device !== null) {
					this.device = device;
					this.categoryName = DeviceCategories.find(dc => dc.Id === device.CategoryId)?.Name;
				} else {
					this.categoryName = '';
				}
			}
		);
		this.deviceModelSub = this.deviceModel$?.subscribe(
			(deviceModel) => {
				this.deviceModel = deviceModel;
				this.deviceFirmware();
			}
		);
		this.deviceInfoSub = this.deviceInfo$?.subscribe(
			(deviceInfo) => {
				this.deviceInfo = deviceInfo;
			}
		);
		this.deviceStatusSub = this.deviceStatus$?.subscribe(
			(deviceStatus) => {
				if (deviceStatus !== undefined) {
					this.deviceOnline = deviceStatus.Online;
					this.deviceConfigured = deviceStatus.Configured === 0;
					this.deviceStatus = deviceStatus;
				}
			}
		);
		this.isSupportTimersSub = this.isSupportTimers$.subscribe((isSupportTimers => {
			this.isSupportTimers = isSupportTimers;
		}));
		this.hwVersionSub = this.hwVersion$.subscribe((hwVersion => {
			this.hwVer = hwVersion;
			this.deviceFirmware();
		}));
	}

	deviceFirmware(): void {
		if (this.deviceModel !== undefined && this.automationsFirmwaresList !== undefined) {
			switch (this.deviceModel.Id) {
				case EDeviceModel.CAMEKWY_GW: {
					this.deviceFirmwareGateway('camekeygw');
					break;
				}
				case EDeviceModel.MTMACONNECT: {
					this.deviceFirmwareGateway('mtmac');
					break;
				}
				case EDeviceModel.MTMACONNECT_US: {
					this.deviceFirmwareGateway('mtmacus');
					break;
				}
				case EDeviceModel.QBE_AUTOMATION: {
					this.deviceFirmwareGateway('qbe');
					break;
				}
				case EDeviceModel.QBE_DONT_AUTOMATION: {
					this.deviceFirmwareGateway('qbesh');
					break;
				}
				case EDeviceModel.GW4G_RADIO: {
					this.deviceFirmwareGateway('gw4g');
					break;
				}
				case EDeviceModel.GW4G_STANDALONE: {
					this.deviceFirmwareGateway('gw4gs');
					break;
				}
				default: {
					this.deviceFirmwareAutomation();
					break;
				}
			}
		}
	}

	deviceFirmwareAutomation(): void {
		if (this.deviceModel !== undefined && this.automationsFirmwaresList !== undefined && this.hwVer !== null) {
			if (this.automationsFirmwaresList.automations[this.deviceModel.BoardName] !== undefined &&
				this.automationsFirmwaresList.automations[this.deviceModel.BoardName][this.hwVer] !== undefined) {
				this.releaseNoteList = this.automationsFirmwaresList.automations[this.deviceModel.BoardName][this.hwVer].sort(
					(a, b) => this.compareVersions(b.version, a.version) ? 1 : -1);
				this.releaseNoteAvailable = this.releaseNoteList.length > 0;
			}
		}
	}


	deviceFirmwareGateway(gateway: string): void {
		if (this.deviceModel !== undefined && this.automationsFirmwaresList !== undefined) {
			if (this.automationsFirmwaresList.gateways[gateway] !== undefined &&
				this.automationsFirmwaresList.gateways[gateway]['0'] !== undefined) {
				this.releaseNoteList = this.automationsFirmwaresList.gateways[gateway]['0'].sort(
					(a, b) => this.compareVersions(b.version, a.version) ? 1 : -1);
				this.releaseNoteAvailable = this.releaseNoteList.length > 0;
			}
		}
	}

	buttonFw() {
		this.updatingFirmware = true;
		this.store.dispatch(DeviceActions.getStateDeviceByCommandIdReset());
		this.store.dispatch(DeviceActions.getStateDeviceByCommandId({ commandId: 16 }));
		this.stateDeviceByCommandIdSub = this.stateDeviceByCommandId$.subscribe((deviceStateDateTime => {
			if (deviceStateDateTime !== undefined && deviceStateDateTime.CommandId === this.COMMAND_ID_FW_UPDATE) {
				if (deviceStateDateTime.Data !== undefined && deviceStateDateTime.Data.length > 0) {
					const fwVersion = deviceStateDateTime.Data[5] + '.' + deviceStateDateTime.Data[4] + '.' + deviceStateDateTime.Data[6];
					if (this.fwVersion !== fwVersion) {
						this.store.dispatch(DeviceActions.updateFirmwareVersion({ fwVersion: fwVersion, message: this.translate.instant("READ_VERSION_FIRMWARE_DONE") }));
					}
					this.updatingFirmware = false;
				}
			}
		}));
	}

	ngOnDestroy(): void {
		this.userSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.deviceModelSub?.unsubscribe();
		this.deviceInfoSub?.unsubscribe();
		this.deviceStatusSub?.unsubscribe();
		this.isSupportTimersSub?.unsubscribe();
		this.stateDeviceByCommandIdSub?.unsubscribe();
		this.automationsFirmwaresListSub?.unsubscribe();
		this.firmwareUpdateSub?.unsubscribe();
	}

	showReleaseNote(): void {
		this.ref = this.dialogService.open(ReleaseNoteDeviceDialogComponent, {
			header: this.translate.instant('RELEASE_NOTE_DEVICE'),
			width: '70vw',
			breakpoints: { '1120px': '98vw' },
			contentStyle: { overflow: 'auto' },
			closable: false,
			data: {
				releaseNoteDeviceList: this.releaseNoteList,
			},
		});
	}

	get isVirtual(): boolean {
		return this.category([EDeviceCategory.Virtual]);
	}

	get supportSignalLevel(): boolean {
		return (this.deviceModel) ? this.deviceModel.SignalLevel : false;
	}

	get isAutomation(): boolean {
		return this.category([EDeviceCategory.Automation]);
	}
	get serial(): string {
		return (this.deviceInfo) ? this.deviceInfo.Serial : '';
	}
	get peripheralNumber(): number {
		return (this.deviceInfo) ? this.deviceInfo.CrpAddress : undefined;
	}
	get keycode(): string {
		if (this.deviceInfo !== undefined && this.deviceInfo.Keycode !== undefined) {
			return this.deviceInfo.Keycode;
		} else if (this.device !== undefined && this.device.Keycode !== undefined) {
			return this.device.Keycode;
		} else {
			return '';
		}
	}
	get productType(): string {
		return (this.deviceModel) ? this.deviceModel.Description : '';
	}

	get boardType(): string {
		if (this.deviceModel) {
			if (this.deviceModel.ProductBoardName !== undefined) {
				return this.deviceModel.ProductBoardName;
			}
		}
		if (this.deviceInfo) {
			return this.deviceInfo.BoardType;
		}

		return '';
	}

	get hwVersion(): string {
		return (this.deviceInfo) ? this.deviceInfo.HwVersion : '';
	}

	get fwVersion(): string {
		return (this.deviceInfo) ? this.deviceInfo.FwVersion : '';
	}

	get productName(): string {
		return (this.deviceModel) ? this.deviceModel.ProductName : '';
	}

	get deviceError(): string {
		return (this.deviceStatus) ? this.deviceStatus.ErrorDescription : '';
	}

	get maneuvers(): string {
		let openings = 0;
		let closures = 0;
		let data = false;

		if (this.deviceStatus) {
			if (this.deviceStatus.Openings !== undefined) {
				openings = this.deviceStatus.Openings;
				data = true;
			}
			if (this.deviceStatus.Closures !== undefined) {
				closures = this.deviceStatus.Closures;
				data = true;
			}
		}

		return (data) ? (openings + closures).toString() : this.translate.instant('ERROR_62');
	}

	get lastSeen(): Date {
		return (this.deviceStatus) ? this.deviceStatus.LastSeen : undefined;
	}

	get imeiCode(): string {
		return this.deviceInfo.Imei;
	}

	category(categories: number[]): boolean {
		if (this.device === undefined) {
			return false;
		}
		return categories.includes(this.device.CategoryId);
	}

	hasImei(): boolean {
		return (this.deviceInfo) ? this.deviceInfo.Imei !== undefined && this.deviceInfo.Imei !== '' : false;
	}

	hasDatetime(): boolean {
		if (this.device === undefined) {
			return false;
		}
		if (isMtmacModel(this.device.ModelId)) {
			return true;
		}
		if (this.deviceModel === undefined || this.deviceModel.BoardName === undefined || this.deviceModel.BoardName === null) {
			return false;
		}
		if (isZlx24sBoardName(this.deviceModel.BoardName.toUpperCase())) {
			return false;
		}
		if (isZbb3BoardName(this.deviceModel.BoardName.toUpperCase())) {
			return false;
		}
		if (isZlb24sBoardName(this.deviceModel.BoardName.toUpperCase())) {
			return false;
		}
		if (isRbe4BoardName(this.deviceModel.BoardName.toUpperCase())) {
			return false;
		}
		if (isZn8BoardName(this.deviceModel.BoardName.toUpperCase())) {
			return false;
		}
		//return false;
		return this.hasTimers();
	}


	getReleaseNoteTitle(version: string): string {
		if (version.search('b') > 0) {
			return version.substring(0, version.search('b'));
		}
		return version;
	}

	hasUpdateFirmware(): boolean {
		if (this.releaseNoteAvailable && this.releaseNoteList !== undefined && this.releaseNoteList.length > 0 &&
			this.getReleaseNoteTitle(this.releaseNoteList[0].version) !== this.fwVersion) {
			return true;
		}
		return false;
	}

	getColorCard(): string {
		if (this.releaseNoteAvailable && this.releaseNoteList !== undefined && this.releaseNoteList.length > 0 &&
			this.getReleaseNoteTitle(this.releaseNoteList[0].version) !== this.fwVersion) {
			return 'bg-yellow-200';
		}
		return '';
	}


	compareVersions(ver1: string, ver2: string): boolean {
		// prevent missing versions v1 and/or v2
		if (!ver1) return false;
		if (!ver2) return true;

		const a = this.getReleaseNoteTitle(ver1).split('.').map(Number);
		const b = this.getReleaseNoteTitle(ver2).split('.').map(Number);
		const maxLength = Math.max(a.length, b.length);

		for (let i = 0; i < maxLength; i++) {
			const num1 = a[i] || 0; // if an element is missing, use 0 (default)
			const num2 = b[i] || 0;
			if (num1 > num2) {
				return true;
			}
			if (num1 < num2) {
				return false;
			}
		}
		//1.1.29
		//1.1.26

		//2.1.10
		//2.1.9

		return true; // if all parts are equal
	}


	hasTimers(): boolean {
		return (this.device.CurrentlyOwned) &&
			!this.includeCategories([EDeviceCategory.SubGateway, EDeviceCategory.IntercomSystem]) &&
			!this.isIORS485() && this.isSupportTimers;
	}

	isGateway(): boolean {
		return this.device.CategoryId === EDeviceCategory.Gateway;
	}

	includeCategories(categories: number[]): boolean {
		return categories.includes(this.device.CategoryId);
	}

	isIORS485(): boolean {
		return this.device.ModelId === EDeviceModel.IORS485 || this.device.ModelId === EDeviceModel.GEN_IORS485;
	}


	hasProgress() {
		if (this.device === undefined) {
			return false;
		}
		return this.isAutomation && !this.isRbe4() && !this.isRbe24230();
	}

	isRbe4(): boolean {
		return this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.RBE4 || this.deviceModel.Id === EDeviceModel.RBE4030 ||
			this.deviceModel.Id === EDeviceModel.RBE4230 || this.deviceModel.Id === EDeviceModel.GEN_RBE4);
	}
	isRbe24230(): boolean {
		return this.deviceModel !== undefined && (this.deviceModel.Id === EDeviceModel.RBE24230 || this.deviceModel.Id === EDeviceModel.RBE230 ||
			this.deviceModel.Id === EDeviceModel.RBE24 || this.deviceModel.Id === EDeviceModel.GEN_RBE24230);
	}

	hasPeripheralNumber(): boolean {
		return this.category([EDeviceCategory.Automation]);
	}

	hasSerial(): boolean {
		return this.category([
			EDeviceCategory.Gateway, EDeviceCategory.SubGateway, EDeviceCategory.HomeAutomation,
			EDeviceCategory.AlarmSystem, EDeviceCategory.Thermostat, EDeviceCategory.IntercomSystem
		]);
	}

	hasKeycode(): boolean {
		return this.category([
			EDeviceCategory.Gateway, EDeviceCategory.SubGateway, EDeviceCategory.HomeAutomation,
			EDeviceCategory.AlarmSystem, EDeviceCategory.Thermostat, EDeviceCategory.IntercomSystem
		]);
	}
	hasProductType(): boolean {
		return true;
	}

	hasBoardType(): boolean {
		return true && !this.excludeBoardType();
	}

	excludeBoardType(): boolean {
		return (this.deviceModel) ? this.deviceModel.Id in ExcludeModelBoardType : false;
	}

	hasHwVersion(): boolean {
		return this.category([
			EDeviceCategory.Gateway, EDeviceCategory.SubGateway, EDeviceCategory.HomeAutomation,
			EDeviceCategory.AlarmSystem, EDeviceCategory.Thermostat
		]) && !this.excludeHwVersion();
	}

	excludeHwVersion(): boolean {
		return (this.deviceModel) ? this.deviceModel.Id in ExcludeModelHwVersion : false;
	}

	hasFwVersion(): boolean {
		return this.category([
			EDeviceCategory.Gateway, EDeviceCategory.SubGateway, EDeviceCategory.Automation,
			EDeviceCategory.HomeAutomation, EDeviceCategory.AlarmSystem, EDeviceCategory.Thermostat
		]) && !this.excludeFwVersion();
	}

	excludeFwVersion(): boolean {
		return (this.deviceModel) ? this.deviceModel.Id in ExcludeModelFwVersion : false;
	}

	hasProductName(): boolean {
		return this.category([EDeviceCategory.Automation]);
	}

	hasError(): boolean {
		return this.category([EDeviceCategory.Automation]) && ((this.deviceStatus) ? this.deviceStatus.Errored : false);
	}

	hasManeuvers(): boolean {
		if (this.device === undefined) {
			return false;
		}
		return this.category([EDeviceCategory.Automation]) && !this.isRbe4() && !this.isRbe24230();
	}

	hasLastSeen(): boolean {
		return !this.category([
			EDeviceCategory.HomeAutomation, EDeviceCategory.AlarmSystem, EDeviceCategory.Thermostat
		]);
	}

	deviceConfiguredOnline(): boolean {
		return this.deviceConfigured && this.deviceOnline;
	}

	offline(): boolean {
		return !this.deviceStatus.Online;
	}

	onFirmwareUpdate(): boolean {
		return this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_STARTED ||
			this.deviceStatus.FwUpdate === CLOUD_FW_UPGRADE_STATE.CLOUD_FW_UPGRADE_STATE_UPDATING;
	}

	clickUpdateFirmware() {
		this.ref = this.dialogService.open(UpdateFirmwareDialogComponent, {
			header: this.translate.instant('UPDATE_FIRWARE'),
			width: '75vw',
			breakpoints: { '1600px': '98vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	notAvailableSignalLevel() {
		if (isThOneBt(this.device.ModelId)) {
			return true;
		}
		if (isThOneGw(this.device.ModelId) && !this.deviceStatus.Online) {
			return true;
		}
		return false;
	}


	@HostListener('window:resize', ['$event'])
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	col12Responsive(): string {
		return (this.screenWidth <= StandardDesignParameters.MIN_WIDTH_COL6) ? "col-12 p-2" : "col-6 p-2";
	}
}
